// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {failureCode} from '@app/features/platform/utils/ResponseInspection';
import TableGameLeaderboards, {
	LEADERBOARD_PAGE_SIZE,
	type LeaderboardFailure,
	type LeaderboardScope,
	leaderboardKey,
} from '@app/features/table_game_leaderboard/state/TableGameLeaderboards';
import {APIErrorCodes} from '@fluxer/constants/src/ApiErrorCodes';
import {
	TABLE_GAME_LEAGUES,
	TABLE_GAME_RANKED_KINDS,
	type TableGameRankedKind,
} from '@fluxer/constants/src/TableGameConstants';
import type {
	GuildTableGameLeaderboardResponse,
	MyTableGameRatingsResponse,
	TableGameLeaderboardResponse,
} from '@fluxer/schema/src/domains/table_game/TableGameLeaderboardSchemas';
import type {TableGameRewardShowcaseResponse} from '@fluxer/schema/src/domains/table_game/TableGameRewardSchemas';

const logger = new Logger('TableGameLeaderboardCommands');

export async function loadShowcase(): Promise<void> {
	const generation = TableGameLeaderboards.claimShowcase();
	if (generation === null) return;
	try {
		const {body} = await http.get<TableGameRewardShowcaseResponse>(Endpoints.TABLE_GAME_REWARDS);
		TableGameLeaderboards.handleShowcase(generation, body);
	} catch (error) {
		logger.warn('Failed to load the season rewards', error);
		TableGameLeaderboards.handleShowcase(generation, null);
	}
}

function failureOf(error: unknown): LeaderboardFailure {
	if (failureCode(error) === APIErrorCodes.FEATURE_TEMPORARILY_DISABLED) return 'disabled';
	if (error instanceof HttpError && error.status === 403) return 'forbidden';
	return 'error';
}

function pageRequest(scope: LeaderboardScope, offset: number): [string, Record<string, string | number>] {
	if (scope.kind === 'global') {
		return [Endpoints.TABLE_GAME_LEADERBOARD(scope.game), {season: scope.period, offset, limit: LEADERBOARD_PAGE_SIZE}];
	}
	return [
		Endpoints.GUILD_TABLE_GAME_LEADERBOARD(scope.guildId),
		{week: scope.period, offset, limit: LEADERBOARD_PAGE_SIZE},
	];
}

export async function loadNextPage(scope: LeaderboardScope): Promise<void> {
	const key = TableGameLeaderboards.ensureBoard(scope);
	const board = TableGameLeaderboards.getBoard(key);
	if (!board || board.isLoading || !board.hasMore || board.failure) return;
	const {generation, session} = TableGameLeaderboards;
	const offset = board.rows.length;
	const [endpoint, query] = pageRequest(scope, offset);
	TableGameLeaderboards.handlePagePending(key);
	try {
		const {body} = await http.get<TableGameLeaderboardResponse | GuildTableGameLeaderboardResponse>(endpoint, {
			query,
		});
		TableGameLeaderboards.handlePage(key, generation, offset, body);
		if (scope.kind === 'guild' && scope.period === 'current' && 'me' in body) {
			TableGameLeaderboards.rememberGuildWeekRank(session, scope.guildId, body.me?.rank ?? null, body.ends_at);
		}
	} catch (error) {
		logger.warn(`Failed to load the leaderboard ${leaderboardKey(scope)}`, error);
		TableGameLeaderboards.handlePageFailure(key, generation, failureOf(error));
	}
}

export function retry(scope: LeaderboardScope): Promise<void> {
	TableGameLeaderboards.handleRetry(leaderboardKey(scope));
	return loadNextPage(scope);
}

async function fetchMyRatings(): Promise<MyTableGameRatingsResponse | null> {
	try {
		const {body} = await http.get<MyTableGameRatingsResponse>(Endpoints.USER_ME_TABLE_GAME_RATINGS);
		return body;
	} catch (error) {
		logger.warn('Failed to load my table game ratings', error);
		return null;
	}
}

export async function loadMyRatings(): Promise<void> {
	const generation = TableGameLeaderboards.generation;
	const ratings = await fetchMyRatings();
	if (ratings) TableGameLeaderboards.handleMyRatings(generation, ratings);
}

// The highest league, then the furthest in it (RR in Legend, progress below); without a league, the first game.
export function bestRatedGame(ratings: MyTableGameRatingsResponse): TableGameRankedKind {
	let best: {game: TableGameRankedKind; league: number; standing: number} | null = null;
	for (const rating of ratings.ratings) {
		if (!rating.league) continue;
		const league = TABLE_GAME_LEAGUES.indexOf(rating.league);
		const standing = rating.rr ?? rating.progress ?? 0;
		if (!best || league > best.league || (league === best.league && standing > best.standing)) {
			best = {game: rating.game, league, standing};
		}
	}
	return best?.game ?? TABLE_GAME_RANKED_KINDS[0];
}

// Once per session (again on the next call after a failed read): my ratings, then the head of my best game's board. The board request is the modal's own first
// page (offset 0, a full page), so the server answers it from the page cache it shares with every viewer.
export async function loadSummary(): Promise<void> {
	const session = TableGameLeaderboards.claimSummary();
	if (session === null) return;
	const ratings = await fetchMyRatings();
	const game = ratings ? bestRatedGame(ratings) : TABLE_GAME_RANKED_KINDS[0];
	let entries: TableGameLeaderboardResponse['entries'] = [];
	let failed = ratings === null;
	try {
		const {body} = await http.get<TableGameLeaderboardResponse>(Endpoints.TABLE_GAME_LEADERBOARD(game), {
			query: {season: 'current', offset: 0, limit: LEADERBOARD_PAGE_SIZE},
		});
		entries = body.entries;
	} catch (error) {
		logger.warn(`Failed to load the head of the ${game} leaderboard`, error);
		failed = true;
	}
	TableGameLeaderboards.handleSummary(session, ratings, game, entries, failed);
}
